import React from 'react';
import { Check, Images, Link2, Loader2 } from 'lucide-react';
import { useGooglePhotosExport } from '../hooks/useGooglePhotosExport';

type Variant = 'toolbar' | 'share';

export const GooglePhotosSaveButton: React.FC<{
  imageUrl?: string | null;
  fileName?: string;
  watermarkExportsEnabled?: boolean;
  /** toolbar = ComparisonSlider dark chrome; share = light ShareActions row */
  variant?: Variant;
}> = ({ imageUrl, fileName, watermarkExportsEnabled = true, variant = 'toolbar' }) => {
  const {
    connected,
    configured,
    phase,
    message,
    busy,
    save,
    connect,
    dismissMessage,
  } = useGooglePhotosExport({ imageUrl, fileName, watermarkExportsEnabled });

  if (!imageUrl || !configured) return null;

  const isToolbar = variant === 'toolbar';
  const showConnectPrimary = !connected || phase === 'needs-connect';
  const isSaved = phase === 'saved';

  const label = isSaved
    ? 'Saved to Google Photos'
    : phase === 'saving'
      ? 'Saving…'
      : showConnectPrimary
        ? 'Connect Google Photos'
        : 'Save to Google Photos';

  const Icon = isSaved ? Check : showConnectPrimary ? Link2 : Images;

  const onClick = () => {
    if (phase === 'saving') return;
    if (showConnectPrimary) {
      connect();
      return;
    }
    save();
  };

  const toolbarClass = isSaved
    ? 'bg-emerald-700/90 text-stone-50 border-emerald-500'
    : showConnectPrimary
      ? 'bg-stone-800 hover:bg-stone-700 text-teal-200 border-stone-700'
      : 'bg-stone-800 hover:bg-teal-800/80 text-stone-100 border-stone-700 hover:border-teal-600';

  const shareClass = isSaved
    ? 'kin-btn kin-btn-ghost border-emerald-300 text-emerald-800'
    : 'kin-btn kin-btn-ghost';

  return (
    <div className={isToolbar ? 'relative flex flex-col items-stretch sm:items-end gap-1' : 'contents'}>
      <button
        type="button"
        onClick={onClick}
        disabled={busy || !imageUrl}
        title={
          showConnectPrimary
            ? 'Connect Google Photos to save this restoration to your library'
            : 'Save the currently selected restoration to Google Photos'
        }
        className={
          isToolbar
            ? `min-h-10 flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shadow-sm border disabled:opacity-60 ${toolbarClass}`
            : `${shareClass} disabled:opacity-60 min-h-10`
        }
      >
        {phase === 'saving' ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
        ) : (
          <Icon className="w-3.5 h-3.5 shrink-0" />
        )}
        <span className={isToolbar ? 'hidden sm:inline' : undefined}>{label}</span>
        {isToolbar && <span className="sm:hidden">{isSaved ? 'Saved' : showConnectPrimary ? 'Connect' : 'Photos'}</span>}
      </button>

      {message && (
        <div
          className={
            isToolbar
              ? `absolute top-full right-0 mt-1 z-30 max-w-[16rem] rounded-lg border px-2.5 py-1.5 text-[11px] leading-snug shadow-lg ${
                  phase === 'saved'
                    ? 'bg-emerald-950/95 border-emerald-700 text-emerald-100'
                    : phase === 'needs-connect'
                      ? 'bg-stone-950/95 border-teal-800 text-teal-100'
                      : 'bg-stone-950/95 border-rose-800 text-rose-100'
                }`
              : 'kin-muted text-[11px]'
          }
          role="status"
        >
          <p>{message}</p>
          {phase === 'needs-connect' && isToolbar && (
            <button
              type="button"
              className="mt-1.5 underline text-teal-300 hover:text-teal-200 font-medium"
              onClick={connect}
            >
              Connect Google Photos
            </button>
          )}
          {(phase === 'error' || phase === 'needs-connect') && (
            <button
              type="button"
              className={
                isToolbar
                  ? 'mt-1 block text-[10px] text-stone-400 hover:text-stone-200'
                  : 'ml-2 underline'
              }
              onClick={dismissMessage}
            >
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
};
